import { useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import type { AangemeldeGebruiker, AanmeldProvider } from './typen.ts';

export const authSleutels = {
  providers: ['auth', 'providers'] as const,
  uitnodiging: (token: string) => ['auth', 'uitnodiging', token] as const,
};

/**
 * Haalt de aangemelde gebruiker op. Geeft `null` als er geen geldige sessie is.
 * @returns De aangemelde gebruiker met sessie en CSRF-token, of `null`.
 */
export async function laadAangemeldeGebruiker(): Promise<AangemeldeGebruiker | null> {
  const { data, response } = await api.GET('/api/v1/auth/me');
  if (response.status === 401) {
    return null;
  }
  if (!response.ok || !data) {
    throw new Error(`Aanmeldstatus niet op te halen (${response.status})`);
  }
  return data;
}

/**
 * Beëindigt de huidige sessie.
 * @param overal Of ook de sessies op andere apparaten eindigen.
 */
export async function afmelden(overal = false): Promise<void> {
  await uitvoeren(api.POST('/api/v1/auth/logout', { body: { overal } }));
}

/**
 * Vervangt het sessietoken en het CSRF-token en verlengt de sessie.
 * @returns De verlengde sessie en het nieuwe CSRF-token.
 */
export async function sessieVerversen() {
  return uitvoeren(api.POST('/api/v1/auth/refresh'));
}

/**
 * Start het koppelen van een extra aanmeldmethode. De browser moet daarna naar het teruggegeven adres.
 * @param provider De sleutel van de provider.
 * @param terugNaar Het adres in de frontend om na afloop naar terug te keren.
 * @returns Het adres van de provider.
 */
export async function koppelenStarten(provider: string, terugNaar: string) {
  return uitvoeren(api.POST('/api/v1/auth/link/{provider}', { params: { path: { provider } }, body: { terugNaar } }));
}

/**
 * Verwijdert een gekoppelde aanmeldmethode.
 * @param provider De sleutel van de provider.
 */
export async function ontkoppelen(provider: string): Promise<void> {
  await uitvoeren(api.DELETE('/api/v1/auth/link/{provider}', { params: { path: { provider } } }));
}

/**
 * De ingeschakelde aanmeldmethoden. Openbaar, dus ook zonder sessie op te halen.
 * @returns De query met de providers.
 */
export function useAanmeldProviders() {
  return useQuery({
    queryKey: authSleutels.providers,
    queryFn: () => uitvoeren<AanmeldProvider[]>(api.GET('/api/v1/auth/providers')),
    staleTime: 5 * 60_000,
  });
}

/**
 * Controleert een uitnodiging en geeft de adressen om haar te gebruiken.
 * @param token Het token uit de uitnodigingslink.
 * @returns De query met de naam van de uitgenodigde en de aanmeldadressen.
 */
export function useUitnodiging(token: string) {
  return useQuery({
    queryKey: authSleutels.uitnodiging(token),
    queryFn: () => uitvoeren(api.GET('/api/v1/auth/uitnodigingen/{token}', { params: { path: { token } } })),
    retry: false,
  });
}

/**
 * Bouwt het adres om een aanmelding te starten, met het adres in de frontend om naar terug te keren.
 * @param aanmeldUrl Het adres van de aanmeldmethode uit de API.
 * @param terugNaar Het pad in de frontend, standaard de startpagina.
 * @returns Het volledige adres voor de browser.
 */
export function aanmeldAdres(aanmeldUrl: string, terugNaar = '/'): string {
  const adres = new URL(aanmeldUrl, window.location.origin);
  adres.searchParams.set('terugNaar', new URL(terugNaar, window.location.origin).href);
  return adres.href;
}
